import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getClassicSpotifyAlbums } from "@/lib/classic-spotify.functions";
import { albumSlug } from "@/lib/slugify";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";

const PAGE_TITLE = "Discos de vinil clássicos | Catálogo Viny Club";
const PAGE_DESC =
  "Catálogo público de discos de vinil clássicos: fichas técnicas com artista, ano, gravadora e gêneros. Curadoria da comunidade para colecionadores de vinil.";

export const Route = createFileRoute("/discos-de-vinil/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["spotify", "classics"],
      queryFn: () => getClassicSpotifyAlbums(),
      staleTime: 1000 * 60 * 60 * 6,
    }),
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:url", content: "https://viny.club/discos-de-vinil" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://viny.club/discos-de-vinil" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Discos de vinil clássicos",
          description: PAGE_DESC,
          url: "https://viny.club/discos-de-vinil",
        }),
      },
    ],
  }),
  component: CatalogIndex,
});

function CatalogIndex() {
  const fn = useServerFn(getClassicSpotifyAlbums);
  const { data: albums = [] } = useQuery({
    queryKey: ["spotify", "classics"],
    queryFn: () => fn(),
    staleTime: 1000 * 60 * 60 * 6,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-6xl px-6 pt-28 pb-16">
        <header className="mb-10">
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Catálogo
          </p>
          <h1 className="text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            Discos de vinil clássicos
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Fichas técnicas de prensagens icônicas, curadas pela comunidade para colecionadores de
            vinil do Viny Club. Cada disco tem capa, gravadora, ano e link para começar sua
            coleção.
          </p>
        </header>

        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
          {albums.map((a) => {
            const slug = albumSlug(a.artist, a.title);
            return (
              <li key={a.id}>
                <Link
                  to="/discos-de-vinil/$slug"
                  params={{ slug }}
                  className="group block"
                >
                  <div className="aspect-square overflow-hidden rounded-md bg-muted">
                    <img
                      src={a.cover}
                      alt={`Capa do disco de vinil ${a.title} de ${a.artist}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="mt-3">
                    <div className="text-sm font-medium line-clamp-1">{a.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {a.artist} {a.year ? `· ${a.year}` : ""}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
